import { lazy } from 'react';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import {
  GeneralLayout,
  AccessControlLayout,
  AuthCheckLayout,
  ProfileCheckLayout,
  RequiredAuthLayout,
  FormBuilderLayout,
  ChatLayout,
  VisitNoteTemplateLayout,
  IsabelLayout,
  OrganizationAccessLayout,
  ConsultsPerMonthAccessLayout,
} from '@/layouts';
import { Layout as FirstLoginLayout } from '@/pages/FirstLogin/layouts';
import { ERouterNames, EUserRoles } from '@/types';
import { AppointmentPreJoinLayout } from '@/layouts/AppointmentPreJoinLayout';

const AccountSettingsPage = lazy(
  () => import('../pages/AccountSettings/pages/AccountSettingsPage'),
);

const Calendar = lazy(() => import('../pages/AccountSettings/pages/Calendar'));
const EPrescribe = lazy(
  () => import('../pages/AccountSettings/pages/EPrescribe'),
);

const Chat = lazy(() => import('../pages/Chats/pages/Chat'));
const MedicalStaffChat = lazy(
  () => import('../pages/Chats/pages/MedicalStaffChat'),
);

const DashboardRoleControl = lazy(
  () => import('../pages/Dashboard/pages/DashboardRoleControl'),
);

const Encounter = lazy(() => import('../pages/Encounters/pages/Encounter'));

const MedicalStaffEncounter = lazy(
  () => import('../pages/Encounters/pages/MedicalStaffEncounter'),
);

const FormRoleUniversal = lazy(
  () => import('../pages/FirstLogin/pages/FormRoleUniversal'),
);

const InstitutionSettings = lazy(
  () => import('../pages/AdminPanel/pages/InstitutionSettings'),
);

const OrganizationAdmins = lazy(
  () => import('../pages/AdminPanel/pages/OrganizationAdmins'),
);

const OrganizationReporting = lazy(
  () => import('../pages/OrganizationReporting'),
);

const InviteLink = lazy(() => import('../pages/InviteLink'));

const MyAccount = lazy(
  () => import('../pages/AccountSettings/pages/MyAccount'),
);

const NotificationsAccountSettings = lazy(
  () => import('../pages/AccountSettings/pages/NotificationsAccountSettings'),
);

const PastSessions = lazy(() => import('../pages/PastSessions'));

const Providers = lazy(() => import('../pages/Providers'));

const CreateAppointment = lazy(
  () => import('../pages/Dashboard/pages/CreateAppointment'),
);

const AppointmentConfirmation = lazy(
  () => import('../pages/Dashboard/pages/AppointmentConfirmation'),
);

const UserAgreement = lazy(
  () => import('../pages/FirstLogin/pages/UserAgreement'),
);

const UsersSettings = lazy(
  () => import('../pages/AdminPanel/pages/UsersSettings'),
);

const ChartWrapper = lazy(
  () => import('../pages/DataReporting/pages/ChartWrapper'),
);

const DataReporting = lazy(
  () => import('../pages/DataReporting/pages/DataReporting'),
);

const VitalTrac = lazy(() => import('@/pages/VitalTrac'));

const MyData = lazy(() => import('@/pages/MyData'));

const PaymentCallbackPage = lazy(
  () => import('../pages/Payments/pages/PaymentCallbackPage'),
);

const PaymentInitialize = lazy(
  () => import('../pages/Payments/pages/PaymentInitialize'),
);

const WaitingRoom = lazy(
  () => import('../pages/WaitingRooms/pages/WaitingRoom'),
);

const Patients = lazy(() => import('../pages/Patients'));

const Patient = lazy(() => import('../pages/Patients/pages/Patient'));

const PatientDosespot = lazy(
  () => import('../pages/Patients/pages/PatientDosespot'),
);

const SessionEnded = lazy(() => import('../pages/SessionEnded'));

const DocumentManagement = lazy(() => import('@/pages/DocumentManagement'));

const FormBuilder = lazy(
  () => import('@/pages/DocumentManagement/pages/FormBuilder'),
);

const FormBuilderPreview = lazy(
  () => import('@/pages/DocumentManagement/pages/FormBuilderPreview'),
);

const FormBuilderEntry = lazy(
  () => import('@/pages/FormBuilderEntries/pages/FormBuilderEntry'),
);

const VisitNotesTemplates = lazy(() => import('@/pages/VisitNotesTemplates'));
const VisitNoteTemplateEdit = lazy(
  () => import('@/pages/VisitNotesTemplates/pages/VisitNoteTemplateEdit'),
);
const VisitNoteTemplatePreview = lazy(
  () => import('@/pages/VisitNotesTemplates/pages/VisitNoteTemplatePreview'),
);

const CommandCenter = lazy(() => import('@/pages/CommandCenter'));

const ScheduleAndOutreach = lazy(() => import('@/pages/ScheduleAndOutreach'));

const Provider = lazy(() => import('@/pages/Providers/pages/Provider'));

const Isabel = lazy(() => import('@/pages/Dashboard/pages/Isabel'));

export const privateRouters: RouteObject[] = [
  {
    element: <RequiredAuthLayout />,
    children: [
      {
        element: <ProfileCheckLayout />,
        children: [
          {
            path: ERouterNames.ROUTE_ME,
            element: <AuthCheckLayout />,
          },
          {
            element: <GeneralLayout />,
            children: [
              {
                element: (
                  <AccessControlLayout
                    roles={[
                      EUserRoles.PATIENT,
                      EUserRoles.PROVIDER,
                      EUserRoles.COORDINATOR,
                      EUserRoles.DEVICE,
                      EUserRoles.PRESENTER,
                    ]}
                  />
                ),
                children: [
                  {
                    path: ERouterNames.APPOINTMENT__JOIN,
                    element: <AppointmentPreJoinLayout />,
                  },
                  {
                    path: ERouterNames.FORM_BUILDER_ENTRY_EDIT,
                    element: <FormBuilderEntry variant="edit" />,
                  },
                  {
                    path: ERouterNames.FORM_BUILDER_ENTRY_VIEW,
                    element: <FormBuilderEntry variant="view" />,
                  },
                  {
                    path: ERouterNames.DASHBOARD,
                    element: <Outlet />,
                    children: [
                      { index: true, element: <DashboardRoleControl /> },
                      {
                        path: ERouterNames.DASHBOARD__SCHEDULE_APPOINTMENT,
                        element: <CreateAppointment scheduleType="scheduled" />,
                      },
                      {
                        path: ERouterNames.DASHBOARD__APPOINTMENT_CONFIRMATION,
                        element: <AppointmentConfirmation />,
                      },
                      {
                        path: ERouterNames.DASHBOARD__SEE_PROVIDER_NOW,
                        element: <CreateAppointment scheduleType="on_demand" />,
                      },
                      {
                        path: ERouterNames.DASHBOARD__ISABEL,
                        element: <IsabelLayout />,
                        children: [
                          {
                            index: true,
                            element: <Isabel />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                element: (
                  <AccessControlLayout
                    roles={[EUserRoles.PROVIDER, EUserRoles.COORDINATOR]}
                  />
                ),
                children: [
                  {
                    path: ERouterNames.SESSIONS,
                    element: <PastSessions />,
                  },
                  {
                    path: ERouterNames.PATIENTS,
                    element: <Patients />,
                  },
                  {
                    path: ERouterNames.PATIENT,
                    element: <Patient />,
                  },
                  {
                    path: ERouterNames.PATIENT_DOSESPOT,
                    element: <PatientDosespot />,
                  },
                ],
              },
              {
                element: (
                  <AccessControlLayout
                    roles={[EUserRoles.PROVIDER, EUserRoles.COORDINATOR]}
                  />
                ),
                children: [
                  {
                    path: ERouterNames.PROVIDERS,
                    element: <Providers />,
                  },
                ],
              },
              {
                element: (
                  <AccessControlLayout roles={[EUserRoles.COORDINATOR]} />
                ),
                children: [
                  {
                    path: ERouterNames.COMMAND_CENTER,
                    element: <CommandCenter />,
                  },
                  {
                    path: ERouterNames.SCHEDULE_AND_OUTREACH,
                    element: <ScheduleAndOutreach />,
                  },
                  {
                    path: ERouterNames.PROVIDER,
                    element: <Provider />,
                  },
                ],
              },
              {
                element: <AccessControlLayout roles={[EUserRoles.ADMIN]} />,
                children: [
                  {
                    path: ERouterNames.USERS_TABLE,
                    element: <UsersSettings />,
                  },
                  {
                    path: ERouterNames.INSTITUTION_SETTINGS,
                    element: <InstitutionSettings />,
                  },
                  {
                    path: ERouterNames.DATA_REPORTING,
                    element: <DataReporting />,
                    children: [
                      {
                        index: true,
                        element: (
                          <Navigate to={ERouterNames.DATA_REPORTING} replace />
                        ),
                      },
                      {
                        path: ERouterNames.DATA_REPORTING__REPORT,
                        element: <ChartWrapper />,
                      },
                    ],
                  },
                  {
                    path: ERouterNames.VISIT_NOTES_TEMPLATES,
                    element: <VisitNotesTemplates />,
                  },
                  {
                    element: <VisitNoteTemplateLayout />,
                    children: [
                      {
                        path: ERouterNames.VISIT_NOTE_TEMPLATE_EDIT,
                        element: <VisitNoteTemplateEdit />,
                      },
                      {
                        path: ERouterNames.VISIT_NOTE_TEMPLATE_PREVIEW,
                        element: <VisitNoteTemplatePreview />,
                      },
                    ],
                  },
                  {
                    path: ERouterNames.DOCUMENT_MANAGEMENT,
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <DocumentManagement />,
                      },
                      {
                        element: <FormBuilderLayout />,
                        children: [
                          {
                            path: ERouterNames.FORM_BUILDER,
                            element: <FormBuilder />,
                          },
                          {
                            path: ERouterNames.FORM_BUILDER_PREVIEW,
                            element: <FormBuilderPreview />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                element: <OrganizationAccessLayout />,
                children: [
                  {
                    path: ERouterNames.ORGANIZATION_ADMINS,
                    element: <OrganizationAdmins />,
                  },
                  {
                    element: <ConsultsPerMonthAccessLayout />,
                    children: [
                      {
                        path: ERouterNames.ORGANIZATION_REPORTING,
                        element: <OrganizationReporting />,
                      },
                    ],
                  },
                ],
              },
              {
                path: ERouterNames.INVITE_LINK,
                element: <InviteLink />,
              },
              {
                element: <ChatLayout />,
                children: [
                  {
                    path: ERouterNames.WAITING_ROOM,
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <WaitingRoom />,
                      },
                      {
                        path: ERouterNames.LEFT,
                        element: <SessionEnded variant="waitingRoom" />,
                      },
                    ],
                  },
                  {
                    path: ERouterNames.MEDICAL_STAFF_CHAT,
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <MedicalStaffChat />,
                      },
                      {
                        path: ERouterNames.SESSION_ENDED,
                        element: <SessionEnded variant="adHoc" />,
                      },
                    ],
                  },
                  {
                    path: ERouterNames.CHAT,
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <Chat />,
                      },
                      {
                        path: ERouterNames.SESSION_ENDED,
                        element: <SessionEnded variant="session" />,
                      },
                    ],
                  },
                  {
                    path: ERouterNames.ENCOUNTER,
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <Encounter />,
                      },
                      {
                        path: ERouterNames.SESSION_ENDED,
                        element: <SessionEnded variant="session" />,
                      },
                    ],
                  },
                  {
                    path: ERouterNames.MEDICAL_STAFF_ENCOUNTER,
                    element: <Outlet />,
                    children: [
                      {
                        index: true,
                        element: <MedicalStaffEncounter />,
                      },
                      {
                        path: ERouterNames.SESSION_ENDED,
                        element: <SessionEnded variant="adHoc" />,
                      },
                    ],
                  },
                ],
              },
              {
                path: ERouterNames.PAYMENTS_INITIALIZE,
                element: <PaymentInitialize />,
              },
              {
                path: ERouterNames.PAYMENT_CALLBACK,
                element: <PaymentCallbackPage />,
              },
              {
                path: ERouterNames.VITALTRAC,
                element: <VitalTrac />,
              },
              {
                path: ERouterNames.MY_DATA,
                element: <MyData />,
              },
            ],
          },
          {
            path: ERouterNames.ACCOUNT_SETTINGS,
            element: <AccountSettingsPage />,
            children: [
              {
                index: true,
                element: (
                  <Navigate
                    to={ERouterNames.ACCOUNT_SETTINGS__MY_ACCOUNT}
                    replace
                  />
                ),
              },
              {
                path: ERouterNames.ACCOUNT_SETTINGS__MY_ACCOUNT,
                element: <MyAccount />,
              },
              {
                path: ERouterNames.ACCOUNT_SETTINGS__NOTIFICATIONS,
                element: <NotificationsAccountSettings />,
              },
              {
                element: <AccessControlLayout roles={[EUserRoles.PROVIDER]} />,
                children: [
                  {
                    path: ERouterNames.ACCOUNT_SETTINGS__CALENDAR,
                    element: <Calendar />,
                  },
                  {
                    path: ERouterNames.ACCOUNT_SETTINGS__EPRESCRIBE,
                    element: <EPrescribe />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: ERouterNames.FIRST_LOGIN,
        element: <FirstLoginLayout />,
        children: [
          {
            index: true,
            element: <UserAgreement />,
          },
          {
            path: ERouterNames.FIRST_LOGIN__PARTICIPANT_FORM,
            element: <FormRoleUniversal />,
          },
        ],
      },
    ],
  },
];
